// Etapa 4: Confirmação e Envio

import React, { useState, useRef } from "react";
import {
  HiDownload,
  HiInformationCircle,
  HiUpload,
  HiX,
  HiCheckCircle,
} from "react-icons/hi";
import { useReactToPrint } from "react-to-print";
import {
  OrcamentoFornecedorData,
  DadosIdentificacao,
  DadosValores,
  DadosAssinatura,
  DadosAceite,
} from "../../../types/orcamento-fornecedor.types";
import {
  formatCurrency,
  formatCNPJ,
  formatTelefone,
  formatPeriodoEvento,
} from "../../../utils/orcamento-formatters";
import { orcamentoFornecedorService } from "../../../services/orcamentoFornecedorService";

interface Etapa4Props {
  dadosOrcamento: OrcamentoFornecedorData;
  dadosIdentificacao: Partial<DadosIdentificacao>;
  dadosValores: Partial<DadosValores>;
  dadosAssinatura: Partial<DadosAssinatura>;
  aceites: Partial<DadosAceite>;
  onAceiteChange: (termo: "termo1" | "termo2", valor: boolean) => void;
  errors: Record<string, string>;
  token?: string;
  onPdfContentReady?: (htmlContent: any) => void; // Callback para enviar HTML ao finalizar
  onFinalizar?: () => void; // Função para enviar orçamento
  isLoading?: boolean; // Estado de loading do envio
}

export const Etapa4Confirmacao: React.FC<Etapa4Props> = ({
  dadosOrcamento,
  dadosIdentificacao,
  dadosValores,
  dadosAssinatura,
  aceites,
  onAceiteChange,
  errors,
  token,
  onPdfContentReady,
  onFinalizar,
  isLoading = false,
}) => {
  const [logoEmpresa, setLogoEmpresa] = useState<string>("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const pdfContentRef = useRef<HTMLDivElement>(null);

  // Carregar logo salva ao montar componente
  React.useEffect(() => {
    if (dadosOrcamento?.logoUrl) {
      setLogoEmpresa(dadosOrcamento?.logoUrl);
    }
  }, [dadosOrcamento]);

  const handlePrint = useReactToPrint({
    contentRef: pdfContentRef,
    documentTitle: `orcamento_${dadosOrcamento.id}`,
  });

  // Notificar quando o conteúdo HTML estiver pronto para conversão
  React.useEffect(() => {
    if (pdfContentRef.current && onPdfContentReady) {
      // Capturar HTML completo com estilos inline
      // const htmlContent = pdfContentRef.current.outerHTML;
      onPdfContentReady(pdfContentRef);
      // onPdfContentReady(htmlContent);
    }
  }, [pdfContentRef.current, onPdfContentReady]);

  // Todos os CNAEs escolhidos, na ordem da lista da empresa.
  const cnaesEscolhidos = dadosOrcamento.fornecedor.cnaes.filter((c) =>
    (dadosIdentificacao.cnaeIds ?? []).includes(c.id),
  );

  // O telefone do documento é o que o fornecedor informou na etapa 1. O do
  // cadastro é só ponto de partida, e em boa parte da base ele nem existe.
  const telefoneDoDocumento =
    dadosIdentificacao.telefoneEmpresa ||
    dadosOrcamento.fornecedor.telefone ||
    "";

  const calcularSubtotal = (
    quantidade: number,
    diaria: number,
    unitario: number,
  ) => {
    return quantidade * diaria * unitario;
  };

  /**
   * Total somado das mesmas linhas que a tabela exibe.
   *
   * Antes vinha de `dadosValores.totalGeral`, que só era atualizado quando o
   * fornecedor mexia no valor de algum item ou ao salvar a etapa 2. Num reenvio
   * ele reabre o link com a etapa 2 já concluída e pode ir direto à confirmação
   * pelo indicador de progresso — e se a quantidade ou as diárias do item
   * mudaram, o rodapé mostrava o total da proposta anterior enquanto as linhas
   * mostravam os valores novos. Esse rodapé vai para o PDF, que é gerado a
   * partir daqui antes de o servidor recalcular.
   *
   * Somando as próprias linhas, total e itens não têm como divergir.
   */
  const totalGeral = dadosOrcamento.itens.reduce(
    (soma, item) =>
      soma +
      calcularSubtotal(
        item.quantidade,
        item.diaria,
        dadosValores.itens?.[item.id] || 0,
      ),
    0,
  );

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar arquivo
    const validationError = orcamentoFornecedorService.validateImageFile(
      file,
      2,
    );
    if (validationError) {
      alert(validationError);
      return;
    }

    // Preview local
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataURL = event.target?.result as string;
      setLogoEmpresa(dataURL);
    };
    reader.readAsDataURL(file);

    // Upload para API (opcional)
    if (token) {
      try {
        setUploadingLogo(true);
        await orcamentoFornecedorService.uploadLogo(token, file);
      } catch (error: any) {
        console.error("Erro ao fazer upload da logo:", error);
        alert(
          "Erro ao fazer upload da logo. A imagem será usada apenas no preview.",
        );
      } finally {
        setUploadingLogo(false);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Estilos de impressão */}
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            
            /* Evitar quebras de página dentro de elementos */
            .page-break-inside-avoid {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            
            /* Garantir que blocos importantes fiquem juntos */
            .signature-block,
            .company-info-block,
            .validity-block {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            
            /* Força quebra antes da assinatura se necessário */
            .signature-block {
              page-break-before: auto;
            }
            
            /* Evitar quebra de linha órfã nas tabelas */
            table {
              page-break-inside: auto;
            }
            
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            
            thead {
              display: table-header-group;
            }
            
            tfoot {
              display: table-footer-group;
            }
            
            /* Garantir que o header do documento não quebre */
            .document-header {
              page-break-after: avoid;
            }
            
            /* Forçar nova página antes de Dados da Empresa */
            .company-info-block {
              margin-top: 1rem;
              page-break-before: always;
              break-before: always;
            }
          }
        `}
      </style>

      {/* Info Alert */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3 no-print">
        <HiInformationCircle className="h-6 w-6 text-[#4760e0] shrink-0 mt-0.5" />
        <div>
          <h3 className="font-bold text-[#161519] mb-1">
            Pré-visualização do Orçamento
          </h3>
          <p className="text-sm text-gray-600">
            Revise todas as informações antes de enviar. Este é o documento que
            será gerado.
          </p>
        </div>
      </div>

      {/* Preview do Documento */}
      <div
        ref={pdfContentRef}
        className="bg-white rounded-lg border border-gray-200 p-8"
      >
        {/* Header do Fornecedor com Logo */}
        <div className="flex justify-between items-start border-b-2 border-gray-200 pb-6 mb-6 document-header page-break-inside-avoid">
          <div className="flex-1">
            {/* Razão social antes do nome: este bloco é o PDF que fica
                salvo como proposta, e documento vale pela pessoa jurídica.
                `nome` guarda o fantasia quando ele existe, então a ordem
                antiga punha o fantasia no título quase sempre. */}
            <h2 className="text-2xl font-bold text-[#161519]">
              {dadosOrcamento.fornecedor.razaoSocial ||
                dadosOrcamento.fornecedor.nome}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              CNPJ: {formatCNPJ(dadosOrcamento.fornecedor.cnpj)}
            </p>
            {dadosOrcamento.fornecedor.endereco && (
              <p className="text-sm text-gray-700 mt-2">
                {dadosOrcamento.fornecedor.endereco}
              </p>
            )}
            {telefoneDoDocumento && (
              <p className="text-sm text-gray-700">
                Tel: {formatTelefone(telefoneDoDocumento)}
              </p>
            )}
            {/* {dadosIdentificacao.responsavel?.email && (
              <p className="text-sm text-gray-700">
                Email: {dadosIdentificacao.responsavel.email}
              </p>
            )} */}
          </div>

          <div className="ml-6">
            {uploadingLogo ? (
              <div className="flex flex-col items-center justify-center h-24 w-24 border-2 border-gray-200 rounded-lg">
                <span className="loading loading-spinner loading-sm"></span>
                <span className="text-xs text-gray-500 mt-1">Enviando...</span>
              </div>
            ) : !!logoEmpresa ? (
              <div className="relative">
                <img
                  src={logoEmpresa}
                  alt="Logo da empresa"
                  className="h-24 w-24 object-contain border-2 border-gray-200 rounded-lg p-2"
                />
                <button
                  onClick={() => setLogoEmpresa("")}
                  className="no-print absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <HiX className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-24 w-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#4760e0] transition-all">
                <HiUpload className="h-6 w-6 text-gray-400" />
                <span className="text-xs text-gray-500 mt-1">Logo</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>

        {/* Destinatário */}
        <div className="mb-6 page-break-inside-avoid">
          <p className="font-semibold text-gray-900">À Petrobras</p>
          <p className="text-sm text-gray-700">
            A/C Monumenta Comunicação e Estratégias Sociais LTDA.
          </p>
          <p className="text-sm text-gray-700">
            SEPS 702/902 - Conjunto B, Bloco B 4º Andar Edifício General
            Alencastro
          </p>
          <p className="text-sm text-gray-700">
            Asa Sul, Brasília - DF, 70390-025
          </p>
          <p className="font-medium text-gray-900 mt-4">
            Conforme solicitado, segue orçamento para:
          </p>
        </div>

        {/* Dados do Projeto */}
        <div className="mb-6 bg-gray-50 p-4 rounded-lg page-break-inside-avoid">
          <p className="text-sm">
            <span className="font-semibold text-gray-700">Cliente:</span>{" "}
            <span className="text-gray-900">
              {dadosOrcamento.planilha.cliente.nome}
            </span>
          </p>
          <p className="text-sm mt-1">
            <span className="font-semibold text-gray-700">Projeto/Evento:</span>{" "}
            <span className="text-gray-900">
              {dadosOrcamento.planilha.evento}
            </span>
          </p>
          {formatPeriodoEvento(
            dadosOrcamento.planilha.dataInicio,
            dadosOrcamento.planilha.dataFim,
          ) && (
            <p className="text-sm mt-1">
              <span className="font-semibold text-gray-700">
                Período do Evento:
              </span>{" "}
              <span className="text-gray-900">
                {formatPeriodoEvento(
                  dadosOrcamento.planilha.dataInicio,
                  dadosOrcamento.planilha.dataFim,
                )}
              </span>
            </p>
          )}
        </div>

        {/* Tabela de Itens */}
        <div className="overflow-x-auto mb-6 page-break-inside-avoid">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700">
                  Item
                </th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700">
                  Descrição
                </th>
                <th className="px-3 py-3 text-center text-sm font-semibold text-gray-700">
                  Qtd
                </th>
                <th className="px-3 py-3 text-center text-sm font-semibold text-gray-700">
                  Diárias
                </th>
                <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700">
                  Valor Unit.
                </th>
                <th className="px-3 py-3 text-right text-sm font-semibold text-gray-700">
                  Subtotal
                </th>
              </tr>
            </thead>
            <tbody>
              {dadosOrcamento.itens.map((item, index) => {
                const valorUnitario = dadosValores.itens?.[item.id] || 0;
                const subtotal = calcularSubtotal(
                  item.quantidade,
                  item.diaria,
                  valorUnitario,
                );

                return (
                  <tr
                    key={item.id}
                    className={`border-b border-gray-100 page-break-inside-avoid ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <td className="px-3 py-3 font-mono font-semibold text-[#4760e0]">
                      {index + 1}
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-medium text-gray-900">
                        {item.descricao}
                      </div>
                      {item.descritivo && (
                        <div className="text-xs text-gray-500 mt-1">
                          {item.descritivo}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center text-gray-700">
                      {item.quantidade}
                    </td>
                    <td className="px-3 py-3 text-center text-gray-700">
                      {item.diaria}
                    </td>
                    <td className="px-3 py-3 text-right text-gray-900">
                      {formatCurrency(valorUnitario)}
                    </td>
                    <td className="px-3 py-3 text-right font-semibold text-gray-900">
                      {formatCurrency(subtotal)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-[#4760e0] text-white">
                <td
                  colSpan={5}
                  className="px-3 py-4 text-right font-bold text-base"
                >
                  TOTAL GERAL:
                </td>
                <td className="px-3 py-4 text-right font-bold text-base">
                  {formatCurrency(totalGeral)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Dados da Empresa e Responsável - Layout lado a lado */}
        <div className="grid grid-cols-2 gap-8 mb-6 border-t-2 border-gray-200 pt-6 company-info-block page-break-inside-avoid">
          {/* Dados da Empresa */}
          <div>
            <h3 className="font-semibold text-[#161519] mb-3">
              Dados da Empresa:
            </h3>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-600">Razão Social:</span>{" "}
                <span className="font-medium text-gray-900">
                  {dadosOrcamento.fornecedor.razaoSocial}
                </span>
              </div>
              <div>
                <span className="text-gray-600">CNPJ:</span>{" "}
                <span className="font-medium text-gray-900">
                  {formatCNPJ(dadosOrcamento.fornecedor.cnpj)}
                </span>
              </div>
              {telefoneDoDocumento && (
                <div>
                  <span className="text-gray-600">Telefone:</span>{" "}
                  <span className="font-medium text-gray-900">
                    {formatTelefone(telefoneDoDocumento)}
                  </span>
                </div>
              )}
              {dadosOrcamento.fornecedor.cep && (
                <div>
                  <span className="text-gray-600">CEP:</span>{" "}
                  <span className="font-medium text-gray-900">
                    {dadosOrcamento.fornecedor.cep}
                  </span>
                </div>
              )}
              {cnaesEscolhidos.length > 0 && (
                <div className="pt-2">
                  <span className="text-gray-600">
                    {cnaesEscolhidos.length > 1 ? "CNAEs:" : "CNAE:"}
                  </span>
                  {/* Um por linha: a descrição é longa, e na mesma linha o
                      texto quebraria no meio de um código. */}
                  {cnaesEscolhidos.map((cnae) => (
                    <div key={cnae.id} className="font-medium text-gray-900">
                      {cnae.codigo} - {cnae.descricao}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Dados do Responsável */}
          <div>
            <h3 className="font-semibold text-[#161519] mb-3">
              Responsável pelo Orçamento:
            </h3>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-600">Nome:</span>{" "}
                <span className="font-medium text-gray-900">
                  {dadosIdentificacao.responsavel?.nome}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Email:</span>{" "}
                <span className="font-medium text-gray-900">
                  {dadosIdentificacao.responsavel?.email}
                </span>
              </div>
              <div>
                <span className="text-gray-600">CPF:</span>{" "}
                <span className="font-medium text-gray-900">
                  {dadosIdentificacao.responsavel?.cpf}
                </span>
              </div>
              <div>
                <span className="text-gray-600">RG:</span>{" "}
                <span className="font-medium text-gray-900">
                  {dadosIdentificacao.responsavel?.rg}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Cargo:</span>{" "}
                <span className="font-medium text-gray-900">
                  {dadosIdentificacao.responsavel?.cargo}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Validade */}
        <div className="text-sm bg-gray-50 p-4 rounded-lg border-t-2 border-gray-200 mt-6 pt-6 validity-block page-break-inside-avoid">
          <p className="mb-2">
            <span className="font-semibold text-gray-700">
              Data do orçamento:
            </span>{" "}
            <span className="text-gray-900">
              {dadosValores.dataOrcamento
                ? new Date(
                    dadosValores.dataOrcamento + "T12:00:00",
                  ).toLocaleDateString("pt-BR")
                : "-"}
            </span>
          </p>
          <p className="italic text-gray-600 mt-2">
            "Este orçamento é válido por {dadosValores.validadeDias || 0} dias a
            partir da data de emissão. Os valores apresentados incluem todos os
            custos relacionados aos serviços descritos."
          </p>
        </div>

        {/* Condições Comerciais */}
        {(dadosValores.formaPagamento ||
          dadosValores.clausulaCancelamento ||
          dadosValores.observacoesProposta) && (
          <div className="mt-6 border-t-2 border-gray-200 pt-6 page-break-inside-avoid">
            <h3 className="font-semibold text-[#161519] mb-3">
              Condições Comerciais
            </h3>
            <div className="space-y-4 text-sm">
              {dadosValores.formaPagamento && (
                <div>
                  <span className="font-medium text-gray-700">
                    Forma de Pagamento:
                  </span>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">
                    {dadosValores.formaPagamento}
                  </p>
                </div>
              )}
              {dadosValores.clausulaCancelamento && (
                <div>
                  <span className="font-medium text-gray-700">
                    Cláusula de cancelamento:
                  </span>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">
                    {dadosValores.clausulaCancelamento}
                  </p>
                </div>
              )}
              {dadosValores.observacoesProposta && (
                <div>
                  <span className="font-medium text-gray-700">
                    Observações:
                  </span>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">
                    {dadosValores.observacoesProposta}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Assinatura - Movida para o final */}
        {dadosAssinatura.assinaturaData && (
          <div className="mt-8 pt-6 border-t-2 border-gray-200 flex justify-center signature-block page-break-inside-avoid">
            <div className="text-center">
              <h3 className="font-semibold text-[#161519] mb-4">Assinatura:</h3>
              <div className="inline-block border-2 border-gray-200 rounded-lg p-4 bg-gray-50 mb-2">
                <img
                  src={dadosAssinatura.assinaturaData}
                  alt="Assinatura"
                  className="max-h-24"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">Assinatura Digital</p>
            </div>
          </div>
        )}
      </div>

      {/* Link discreto para PDF */}
      <div className="flex justify-center mb-4">
        <button
          type="button"
          onClick={handlePrint}
          className="inline-flex items-center gap-2 text-sm text-[#4760e0] hover:text-[#3850c0] hover:underline transition-all cursor-pointer"
        >
          <HiDownload className="h-4 w-4" />
          Imprimir ou salvar como PDF
        </button>
      </div>

      {/* Termos de Aceite */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-[#161519] mb-6 pb-4 border-b border-gray-200">
          Termos e Condições
        </h3>

        <div className="space-y-4">
          <label className="cursor-pointer block">
            <div
              className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-all ${
                aceites.termo2
                  ? "border-[#4760e0] bg-[#4760e0]/5"
                  : "border-gray-200 hover:border-gray-300"
              } ${errors.termo2 ? "border-red-500" : ""}`}
            >
              <input
                type="checkbox"
                checked={aceites.termo2 || false}
                onChange={(e) => {
                  onAceiteChange("termo1", e.target.checked);
                  onAceiteChange("termo2", e.target.checked);
                }}
                className="w-5 h-5 rounded border-2 border-gray-300 text-[#4760e0] focus:ring-2 focus:ring-[#4760e0]/20 mt-0.5 shrink-0"
              />
              <div className="text-sm text-gray-700">
                <p className="font-medium">
                  Confirmo que as informações fornecidas são verdadeiras e
                  concordo com o envio desta cotação
                </p>
              </div>
            </div>
          </label>
        </div>

        {errors.termo2 && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 font-medium">
              Você deve aceitar o termo para continuar
            </p>
          </div>
        )}

        {/* Botão de Envio - Grande e Verde */}
        {onFinalizar && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onFinalizar}
              disabled={isLoading || !aceites.termo2}
              className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              {isLoading ? (
                <>
                  <span className="loading loading-spinner loading-md"></span>
                  {dadosOrcamento.edicaoInterna
                    ? "Salvando cotação..."
                    : "Enviando Orçamento..."}
                </>
              ) : (
                <>
                  <HiCheckCircle className="h-6 w-6" />
                  {dadosOrcamento.edicaoInterna
                    ? "Salvar e gerar novo PDF"
                    : "Enviar Orçamento"}
                </>
              )}
            </button>
            {(!aceites.termo1 || !aceites.termo2) && (
              <p className="text-center text-sm text-gray-500 mt-3">
                Aceite os termos acima para habilitar o envio
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
